import { delay } from "./api";
import { assetUrl, http, orUndefined, storeImage } from "./http";
import type { Activity, Cycle, CycleCode, SchoolInfo, SchoolYear, SchoolPlan } from "@/types";
import { setCurrency } from "@/utils/currency";

/* -------------------------------------------------------------------------- */
/* Format du backend                                                           */
/* -------------------------------------------------------------------------- */

interface ApiSchool {
  name: string;
  address: string | null;
  city: string | null;
  country: string | null;
  phone: string | null;
  email: string | null;
  slogan: string | null;
  schoolYear: string | null;
  logoUrl: string | null;
  signatoryName: string | null;
  signatoryTitle: string | null;
  stateName: string | null;
  stateMotto: string | null;
  ministryName: string | null;
  currencySymbol: string;
  currencyName: string;
  currencyNamePlural: string;
  plan: SchoolPlan;
  subscriptionEndsAt: string | null;
}

interface ApiCycle {
  id: number;
  code: CycleCode;
  label: string;
  description: string | null;
}

interface ApiSchoolYear {
  id: number;
  label: string;
  startDate: string;
  endDate: string;
  isActive: boolean;
}

/** Derniers cycles lus du backend (libellés synchrones, voir `knownCycleLabel`). */
let knownCycles: Cycle[] = [];

/** Libellé d'un cycle déjà chargé depuis le backend (undefined s'il est inconnu). */
export function knownCycleLabel(cycleId: string): string | undefined {
  return knownCycles.find((c) => c.id === cycleId)?.label;
}

/** Année scolaire active : connue après `primeActiveSchoolYear` ou une lecture de l'école / des années. */
let activeYearCache: string | null = null;

/** Années connues (dates comprises) et année CONSULTÉE (choisie dans l'en-tête ; par défaut l'année active). */
let yearsCache: SchoolYear[] = [];
const CONSULTED_KEY = "waitechedu.consultedYear";
let consultedYear: string | null = (() => {
  try {
    return sessionStorage.getItem(CONSULTED_KEY);
  } catch {
    return null;
  }
})();

const toYear = (y: ApiSchoolYear): SchoolYear => ({
  id: String(y.id),
  label: y.label,
  startDate: y.startDate,
  endDate: y.endDate,
  active: y.isActive,
});

function toSchoolInfo(s: ApiSchool): SchoolInfo {
  if (s.schoolYear) activeYearCache = s.schoolYear;
  // Tous les montants affichés suivent la devise de l'école.
  setCurrency({ symbol: s.currencySymbol, name: s.currencyName, plural: s.currencyNamePlural });
  return {
    name: s.name,
    address: s.address ?? "",
    city: s.city ?? "",
    country: s.country ?? "",
    phone: s.phone ?? "",
    email: s.email ?? "",
    slogan: s.slogan ?? "",
    schoolYear: s.schoolYear ?? "",
    logo: assetUrl(s.logoUrl),
    signatoryName: s.signatoryName ?? "",
    signatoryTitle: s.signatoryTitle ?? "",
    stateName: s.stateName ?? "",
    stateMotto: s.stateMotto ?? "",
    ministryName: s.ministryName ?? "",
    currencySymbol: s.currencySymbol ?? "FG",
    currencyName: s.currencyName ?? "franc guinéen",
    currencyNamePlural: s.currencyNamePlural ?? "francs guinéens",
    plan: s.plan ?? "ETABLISSEMENT",
    subscriptionEndsAt: s.subscriptionEndsAt ?? null,
  };
}

/* -------------------------------------------------------------------------- */
/* Établissement                                                               */
/* -------------------------------------------------------------------------- */

export async function getSchoolInfo(): Promise<SchoolInfo> {
  return toSchoolInfo(await http.get<ApiSchool>("/school"));
}

export async function updateSchoolInfo(patch: Partial<SchoolInfo>): Promise<SchoolInfo> {
  // PUT : la fiche est remplacée en entier (les champs absents deviennent vides).
  const next = { ...(await getSchoolInfo()), ...patch };
  const saved = await http.put<ApiSchool>("/school", {
    name: next.name.trim(),
    address: orUndefined(next.address),
    city: orUndefined(next.city),
    country: orUndefined(next.country),
    phone: orUndefined(next.phone),
    email: orUndefined(next.email),
    slogan: orUndefined(next.slogan),
    logoUrl: await storeImage(next.logo),
    signatoryName: orUndefined(next.signatoryName),
    signatoryTitle: orUndefined(next.signatoryTitle),
    stateName: orUndefined(next.stateName),
    stateMotto: orUndefined(next.stateMotto),
    ministryName: orUndefined(next.ministryName),
    currencySymbol: orUndefined(next.currencySymbol),
    currencyName: orUndefined(next.currencyName),
    currencyNamePlural: orUndefined(next.currencyNamePlural),
  });
  return toSchoolInfo(saved);
}

/* -------------------------------------------------------------------------- */
/* Années scolaires & cycles                                                   */
/* -------------------------------------------------------------------------- */

export async function getSchoolYears(): Promise<SchoolYear[]> {
  const years = (await http.getAll<ApiSchoolYear>("/school-years")).map(toYear);
  yearsCache = [...years].sort((a, b) => b.label.localeCompare(a.label));
  activeYearCache = years.find((y) => y.active)?.label ?? null;
  // Une année consultée qui n'existe plus : retour à l'année active.
  if (consultedYear && !years.some((y) => y.label === consultedYear)) setConsultedSchoolYear(null);
  return yearsCache;
}

/** Charge les années et l'année active (à appeler après la connexion). */
export async function primeActiveSchoolYear(): Promise<void> {
  try {
    await getSchoolYears();
  } catch {
    activeYearCache = null;
  }
}

export async function createSchoolYear(payload: Pick<SchoolYear, "label" | "startDate" | "endDate">): Promise<SchoolYear> {
  return toYear(await http.post<ApiSchoolYear>("/school-years", payload));
}

export async function updateSchoolYear(id: string, payload: Pick<SchoolYear, "label" | "startDate" | "endDate">): Promise<SchoolYear> {
  return toYear(await http.put<ApiSchoolYear>(`/school-years/${id}`, payload));
}

/** L'année devient l'année ACTIVE de toute l'école (inscriptions, fiche et paiements par défaut). */
export async function activateSchoolYear(id: string): Promise<SchoolYear> {
  return toYear(await http.patch<ApiSchoolYear>(`/school-years/${id}/activate`));
}

export async function deleteSchoolYear(id: string): Promise<void> {
  await http.delete(`/school-years/${id}`);
}

/** Années connues (de la plus récente à la plus ancienne). */
export function knownSchoolYears(): SchoolYear[] {
  return yearsCache;
}

/** L'année réellement active de l'école (indépendamment de l'année consultée). */
export function getSchoolActiveYear(): string {
  return activeYearCache ?? "";
}

/** Choisit l'année consultée (null : revenir à l'année active). Gardée pour la session de l'onglet. */
export function setConsultedSchoolYear(label: string | null): void {
  consultedYear = label && label !== activeYearCache ? label : null;
  try {
    if (consultedYear) sessionStorage.setItem(CONSULTED_KEY, consultedYear);
    else sessionStorage.removeItem(CONSULTED_KEY);
  } catch {
    /* stockage indisponible : le choix vaut jusqu'au rechargement */
  }
}

/** Dates de l'année consultée (null si inconnues). */
export function getConsultedYearRange(): { from: string; to: string } | null {
  const year = yearsCache.find((y) => y.label === getActiveSchoolYear());
  return year ? { from: year.startDate, to: year.endDate } : null;
}

/** Vrai si l'utilisateur consulte une autre année que l'année active. */
export function isConsultingOtherYear(): boolean {
  return !!consultedYear && consultedYear !== activeYearCache;
}

export async function getCycles(): Promise<Cycle[]> {
  const list = await http.getAll<ApiCycle>("/cycles");
  knownCycles = list.map((c) => ({
    id: String(c.id),
    code: c.code,
    label: c.label,
    description: c.description ?? undefined,
  }));
  return knownCycles;
}

/** Ouvre un cycle (matières et catégories de départ ; année en cours si besoin ; aucune classe). */
export async function openCycle(
  code: CycleCode,
): Promise<{ cycleId: number; subjects: number; schoolYear: string }> {
  return http.post("/school-setup/cycles", { code });
}

const ACTIVITY_ICON: Record<string, string> = {
  CREATE: "ri-add-circle-line",
  UPDATE: "ri-edit-line",
  DELETE: "ri-delete-bin-line",
  LOGIN: "ri-login-box-line",
};

/** Dernières actions de l'école (journal) ; vide si l'utilisateur n'a pas accès au journal. */
export async function getActivities(): Promise<Activity[]> {
  try {
    const rows = await http.get<
      Array<{ id: number; createdAt: string; actorName: string; module: string; action: string; description: string }>
    >("/activity", { limit: 8 });
    return rows.map((r) => ({
      id: String(r.id),
      icon: ACTIVITY_ICON[r.action] ?? "ri-record-circle-line",
      title: r.description,
      detail: `${r.actorName} • ${r.module}`,
      time: new Date(r.createdAt).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }),
      tone: r.action === "DELETE" ? "accent" : r.action === "CREATE" ? "primary" : "secondary",
    }));
  } catch {
    return [];
  }
}

/**
 * Année scolaire de travail des pages : l'année CONSULTÉE (choisie dans l'en-tête), sinon l'année active.
 * Élèves, inscriptions, paiements, fiche, effectifs et finances suivent cette année.
 */
export function getActiveSchoolYear(): string {
  return consultedYear ?? activeYearCache ?? "";
}
